<script setup>
import { computed, markRaw, onMounted, reactive, ref, watch } from "vue";
import DocumentApi from "@api/document.api";
import Document from "@models/document";
import { useValidation } from "@composables/useValidation";
import { required, minLength } from "@services/i18n-validators";
import { helpers } from "@vuelidate/validators";
import { useRoute, useRouter } from "vue-router";
import { useLoaderStore } from "@store/loader";
import useForm from "@composables/Form";
import { ElNotification } from "element-plus";
import Storage from "@services/storage";
import Category1 from "./Category1.vue";
import Category2 from "./Category2.vue";
import Category3 from "./Category3.vue";
import Category4 from "./Category4.vue";
import Category5 from "./Category5.vue";
import Category1Step3 from "./Category1Step3.vue";
import projectDocumentImg from "@/assets/img/project_documents.png";
import supportDocumentImg from "@/assets/img/support_documents.png";
import PartyDocumentImg from "@/assets/img/3rd_party_documents.png";
import otherDocument from "@/assets/img/other_documents.png";
import offeringDocumentImg from "@/assets/img/offering_documents.png";
import completedStep from "@/assets/img/completed-step.png";
import cancelModal from "../../components/cancelModal.vue";
import Swal from "sweetalert2";

const ENTITY_KEY = "__site_Dkms";

// Ensure length checks ignore whitespace characters, reuse minLength message
const minNonWhitespaceLength = (min) => {
    const message = minLength(min).$message;
    const validator = (value) => {
        if (typeof value !== "string") return false;
        const nonWhitespaceLength = value.replace(/\s/g, "").length;
        return nonWhitespaceLength >= min;
    };
    return helpers.withParams(
        { type: "minLength", min },
        helpers.withMessage(message, validator)
    );
};

let {
    isLoading,
    submitted,
    clearErrors,
    formErrors,
    catchErrors,
    catchSuccess,
} = useForm();
const router = useRouter();
const route = useRoute();
const loaderStore = useLoaderStore();
const isUpdateForm = computed(() => !!document.id);

let errors = reactive([]);
let document = reactive(new Document());
const disabled = ref(false);
const isSubmitting = ref(false);
const submitLabel = computed(() => {
    if (isSubmitting.value) return "Saving...";
    return "Upload a Document";
});

const value = ref([]);
const loading = ref(false);
const currentCategory = ref(null);
const selected = ref(null);
const state = reactive({
    clientsForSelect: [],
    industriesForSelect: [],
    serviceLinesForSelect: [],
    offeringForSelect: [],
    leadsForSelect: [],
    teamForSelect: [],
    BusinessUnitsForSelect: [],
    digitalSolutionForSelect: [],
    serviceOfferingForSelect: [],
    businessDomainsForSelect: [],
});

const categories = [
    {
        id: 1,
        label: "Project Documents",
        description: "Documents created during client projects",
        markRaw: Category1,
        customValue: 1,
        modalTitle: "Upload Project Document",
        img: projectDocumentImg,
    },
    {
        id: 2,
        label: "Offering Document",
        description: "Documents codifying TAM Domains and Offerings",
        markRaw: Category2,
        customValue: 2,
        modalTitle: "Upload Offering Document",
        img: offeringDocumentImg,
    },
    {
        id: 3,
        label: "Support Service Documents",
        description:
            "Documents from enabling teams (e.g. Branding, L&D, K&R etc)",
        markRaw: Category4,
        customValue: 4,
        modalTitle: "Support Service Documents",
        img: supportDocumentImg,
    },
    {
        id: 4,
        label: "3rd Party Documents",
        description:
            "Reference documents sourced from outside TAM (e.g. HBR, UN, Other Consulting firms etc)",
        markRaw: Category5,
        customValue: 5,
        modalTitle: "Upload 3rd Party Documents",
        img: PartyDocumentImg,
    },
    {
        id: 5,
        label: "Other <br> Document",
        description: "Miscellaneous files not covered by other categories",
        markRaw: Category3,
        customValue: 3,
        modalTitle: "Upload Other Document",
        img: otherDocument,
    },
];

const emit = defineEmits(["create", "update", "close", "update:title"]);

const props = defineProps({
    defaultCategory: { type: Number, default: null },
    defaultProjectId: { type: Number, default: null },
    autoProceedToStep2: { type: Boolean, default: false },
});

const rulesCategory1 = reactive({
    title: { required, minLength: minNonWhitespaceLength(3) },
    document_type_id: { required },
    document_context: { required },
    document_purpose: { required },
    authors_ids: { required },
    files: { required },
    offering_ids: {},
});
const rulesStep3ForCategory1 = reactive({
    // Make track selection optional for Project category step 3
    track_id: {},
    offering_ids: {},
    business_unit_id: { required },
});
const rulesCategory2 = reactive({
    title: { required },
    document_type_id: { required },
    document_context: { required },
    document_purpose: { required },
    business_unit_id: { required },
    offering_ids: { required },
    business_domain_ids: { required },
    files: { required },
    industry_id: { required },
    authors_ids: { required },
});
const rulesCategory3 = reactive({
    title: { required },
    document_type_id: { required },
    document_context: { required },
    document_purpose: { required },
    business_unit_id: { required },
    offering_ids: { required },
    business_domain_ids: { required },
    files: { required },
    industry_id: { required },
    authors_ids: { required },
});
const rulesCategory4 = reactive({
    title: { required },
    document_type_id: { required },
    document_context: { required },
    document_purpose: { required },
    business_unit_id: { required },
    offering_ids: { required },
    business_domain_ids: { required },
    files: { required },
    industry_id: { required },
    authors_ids: { required },
});
const rulesCategory5 = reactive({
    title: { required },
    document_type_id: { required },
    document_context: { required },
    document_purpose: { required },
    business_unit_id: { required },
    offering_ids: { required },
    business_domain_ids: { required },
    files: { required },
    industry_id: { required },
    authors_ids: { required },
});

const currentRules = computed(() => {
    if (document.category == 1 && active.value === 2) {
        return rulesStep3ForCategory1;
    }
    switch (document.category) {
        case 1:
            return rulesCategory1;
        case 2:
            return rulesCategory2;
        case 3:
            return rulesCategory3;
        case 4:
            return rulesCategory4;
        case 5:
            return rulesCategory5;
        default:
            return {}; // Step 0 no validation
    }
});

const { v$, $externalResults } = useValidation(currentRules, document);

// Control enabling of the Next button for Category 1 (Step 2 -> Step 3)
const canProceedToStep3 = computed(() => {
    if (document.category !== 1) return false;
    if (active.value !== 1) return false;
    // Use actual field values so it remains accurate after back() resets validation
    const hasTitle =
        typeof document.title === "string" &&
        document.title.replace(/\s/g, "").length >= 3;
    const hasType = !!document.document_type_id;
    const hasContext =
        typeof document.document_context === "string" &&
        document.document_context.trim().length > 0;
    const hasPurpose =
        typeof document.document_purpose === "string" &&
        document.document_purpose.trim().length > 0;
    const hasAuthors =
        Array.isArray(document.authors_ids) && document.authors_ids.length > 0;
    const filesLen = Array.isArray(document.files) ? document.files.length : 0;
    const hasFiles =  filesLen > 0;
    return (
        hasTitle &&
        hasType &&
        hasContext &&
        hasPurpose &&
        hasAuthors &&
        hasFiles
    );
});

// Enable Upload button for categories 2–5 only when all required fields are present
const canSubmitStep2 = computed(() => {
    if (active.value !== 1) return false;
    if (document.category === 1) return false;

    const hasTitle =
        typeof document.title === "string" && document.title.trim().length > 0;
    const hasType = !!document.document_type_id;
    const hasContext =
        typeof document.document_context === "string" &&
        document.document_context.trim().length > 0;
    const hasPurpose =
        typeof document.document_purpose === "string" &&
        document.document_purpose.trim().length > 0;
    const hasAuthors =
        Array.isArray(document.authors_ids) && document.authors_ids.length > 0;
    const hasBU = !!document.business_unit_id;
    const hasOffering =
        Array.isArray(document.offering_ids) &&
        document.offering_ids.length > 0;
    const hasDomains =
        Array.isArray(document.business_domain_ids) &&
        document.business_domain_ids.length > 0;
    const hasIndustry = !!document.industry_id;
    const filesLen = Array.isArray(document.files) ? document.files.length : 0;
    const hasFiles =  filesLen > 0;

    // Categories 2–5 share the same required fields defined by rulesCategory2–5
    return (
        hasTitle &&
        hasType &&
        hasContext &&
        hasPurpose &&
        hasAuthors &&
        hasBU &&
        hasOffering &&
        hasDomains &&
        hasIndustry &&
        hasFiles
    );
});

async function submit() {
    if (await v$.value.$validate()) {
        disabled.value = true;
        isSubmitting.value = true;
        await save();
    }
}

function submitDisabled(value) {
    disabled.value = value;
}

async function save() {
    if (document.business_unit_id === undefined) {
        document.business_unit_id = 1;
    }
    const request =
        isUpdateForm && document.id
            ? DocumentApi.update(document.id, document)
            : DocumentApi.store(document);
    request
        .then(({ data }) => {
            Storage.remove(ENTITY_KEY);

            if (isUpdateForm) {
                ElNotification({
                    title: "Success",
                    message: data.message,
                    type: "success",
                    position: "bottom-left",
                });
            }

            emit("create", data.data.document);

            // Don't close the parent modal yet - let the success modal handle it
        })
        .catch((error) => {
            const status = error?.response?.status;
            const resp = error?.response?.data || {};
            const errors = resp?.errors || {};
            const messages = [];
            if (Array.isArray(errors?.message)) {
                messages.push(...errors.message);
            }
            // Collect other field errors too
            Object.keys(errors)
                .filter((k) => k !== "message")
                .forEach((k) => {
                    const v = errors[k];
                    if (Array.isArray(v)) messages.push(...v);
                    else if (typeof v === "string") messages.push(v);
                });
            const html = messages.length
                ? `<ul style="text-align:left;margin:0;padding-left:1.2rem;">${messages
                      .map((m) => `<li>${m}</li>`)
                      .join("")}</ul>`
                : resp?.message || "An error occurred";

            if (status === 422 || status === 500) {
                Swal.fire({
                    title: "Error",
                    html,
                    icon: "error",
                    confirmButtonText: "OK",
                });
            } else {
                ElNotification({
                    title: status ? `Error ${status}` : "Error",
                    message: resp?.message || "An error occurred",
                    type: "error",
                    position: "bottom-left",
                });
            }

            $externalResults.value = error.response?.data?.errors;
            catchErrors(error.response?.data?.errors || {});
        })
        .finally(() => {
            isSubmitting.value = false;
            disabled.value = false;
        });
}

let active = ref(0);
const preventBackToStep1 = ref(false);

const showCancelModal = ref(false);
const showSuccessModal = ref(false);
const createdDocumentId = ref(null);

const cancelButtonClose = () => {
    showCancelModal.value = false;
};

const confirmCancel = () => {
    Storage.remove(ENTITY_KEY);
    showCancelModal.value = false;
    router.replace({ name: "home" });
};

async function close() {
    errors.value = [];
    emit("close");
}

function selectCategory(category) {
    selected.value = category;

    // Clear form data when switching categories (except for update forms)
    if (!isUpdateForm.value) {
        clearFormData();
    }

    emit("update:title", category.modalTitle);
}

function clearFormData() {
    // Reset document to initial state
    Object.assign(document, new Document());
    
    // Clear any validation errors
    if (v$.value) {
        v$.value.$reset();
    }
    
    // Clear form errors
    formErrors.value = {};
    
    // Reset active step
    active.value = 0;
}

async function validateStep() {
    if (active.value === 0) {
        if (!document.category) {
            ElNotification({
                title: "Error",
                message: "Please select a document category",
                type: "error",
                position: "bottom-left",
            });
            return false;
        }
        return true;
    }

    const valid = await v$.value.$validate();
    if (!valid) {
        ElNotification({
            title: "Error",
            message: "Please fill all required fields",
            type: "error",
            position: "bottom-left",
        });
    }
    return valid;
}

async function next() {
    const ok = await validateStep();
    if (!ok) return;

    // Entering step 2 from step 1: ensure files are cleared for a fresh start
    /* if (active.value === 0) {
        document.files = [];
    } */

    if (document.category == 1 && active.value < 2) {
        active.value++;
    } else if (document.category != 1 && active.value < 1) {
        active.value++;
    }
}

function back() {
    // Block navigating back to Step 1 when auto-proceeded
    if (active.value === 1 && preventBackToStep1.value) {
        return;
    }

    if (active.value > 0) {
        const wasStep3InCategory1 =
            document.category == 1 && active.value === 2;
        active.value--;

        // reset all validation errors when going back
        v$.value.$reset();

        formErrors.value = {};

        // If returning from step 3 to step 2 for Category 1, clear uploaded files
        /* if (wasStep3InCategory1) {
            document.files = [];
        } */
    }
}

watch(
    document,
    (newState) => {
        for (const propName in newState) {
            if (propName == "category") {
                continue;
            }

            if (propName != "files" && isEmptyValues(newState[propName])) {
                delete newState[propName];
            } else {
                newState["active"] = 1;
            }
        }

        Storage.set(ENTITY_KEY, newState);
    },
    { deep: true }
);

function isEmptyValues(value) {
    return (
        value === undefined ||
        value === null ||
        value === NaN ||
        (typeof value === "object" && Object.keys(value).length === 0) ||
        (typeof value === "string" && value.trim().length === 0)
    );
}

onMounted(() => {
    const savedState = Storage.get(ENTITY_KEY);

    // if (savedState) {
    //     Object.assign(document, savedState);
    //
    //     if (document.active) {
    //         active.value = 1
    //     }
    //
    //     const category = defineAsyncComponent(() =>
    //         import(`./Category${document.category}.vue`)
    //     )
    //
    //     currentCategory.value = markRaw(category)
    // }

    // if (document.category) {
    //     const found = categories.find(c => c.customValue === document.category);
    //     if (found) emit("update:title", found.modalTitle);
    // }
    // Apply defaults coming from opener (e.g., Project Documents empty state)
    if (props.defaultCategory) {
        document.category = props.defaultCategory;
        const found = categories.find(
            (c) => c.customValue === document.category
        );
        if (found) {
            currentCategory.value = markRaw(found.markRaw);
            selectCategory(found);
        }

        // If requested, proceed directly to step 2
        if (props.autoProceedToStep2) {
            active.value = 1;
            preventBackToStep1.value = true;
        }
    }

    // Preselect project for step 3 when provided
    if (props.defaultProjectId) {
        document.project_id = props.defaultProjectId;
    }
});
</script>

<template>
    <div>
        <!-- Loading Spinner -->
        <div v-if="loaderStore.isLoading" class="loading-overlay">
            <div class="loading-spinner"></div>
        </div>

        <!-- steps-->
        <div
            class="d-flex align-items-center justify-content-between mb-3 stepper"
        >
            <!-- Step 1 -->
            <div class="d-flex align-items-center">
                <div
                    :class="
                        active > 0
                            ? 'text-white'
                            : active === 0
                            ? 'bg-primary text-white'
                            : 'border'
                    "
                    class="step-circle d-flex align-items-center justify-content-center"
                >
                    <template v-if="active > 0">
                        <img :src="completedStep" alt="Completed" />
                    </template>
                    <template v-else> 1 </template>
                </div>
                <span
                    class="font-size-base"
                    :class="
                        active === 0
                            ? 'fw-normal text-primary ms-2'
                            : 'fw-normal text-success ms-2'
                    "
                    style="font-size: 16px; margin-bottom: 8px"
                >
                    Select Document Category
                </span>
            </div>

            <!-- Step 2 -->
            <div class="d-flex align-items-center me-6">
                <div
                    :class="
                        active > 1
                            ? 'text-white'
                            : active === 1
                            ? 'bg-primary text-white'
                            : 'border'
                    "
                    class="step-circle d-flex align-items-center justify-content-center"
                >
                    <template v-if="active > 1">
                        <img :src="completedStep" alt="Completed" />
                    </template>
                    <template v-else> 2 </template>
                </div>
                <span
                    :class="
                        active > 1
                            ? 'text-success ms-2'
                            : active === 1
                            ? 'fw-normal text-primary ms-2'
                            : 'fw-normal text-muted ms-2'
                    "
                    style="font-size: 16px; margin-bottom: 8px"
                >
                    Add Document Details
                </span>
            </div>

            <!-- Step 3 -->
            <div
                v-if="document.category !== 1"
                class="d-flex align-items-center"
            ></div>
            <div
                v-if="document.category == 1"
                class="d-flex align-items-center"
            >
                <div
                    :class="
                        active > 2
                            ? 'bg-primary text-white'
                            : active === 2
                            ? 'bg-primary text-white'
                            : 'border'
                    "
                    class="step-circle d-flex align-items-center justify-content-center"
                >
                    <template v-if="active > 2">
                        <svg
                            height="20"
                            viewBox="0 0 640 640"
                            width="20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M320 112C434.9 112 528 205.1 528 320C528 434.9 434.9 528 320 528C205.1 528 112 434.9 112 320C112 205.1 205.1 112 320 112zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM404.4 276.7C411.4 265.5 408 250.7 396.8 243.6C385.6 236.5 370.8 240 363.7 251.2L302.3 349.5L275.3 313.5C267.3 302.9 252.3 300.7 241.7 308.7C231.1 316.7 228.9 331.7 236.9 342.3L284.9 406.3C289.6 412.6 297.2 416.2 305.1 415.9C313 415.6 320.2 411.4 324.4 404.6L404.4 276.6z"
                                fill="#ffffff"
                            />
                        </svg>
                    </template>
                    <template v-else> 3 </template>
                </div>
                <span
                    :class="
                        active === 2
                            ? 'fw-normal text-primary ms-2'
                            : 'fw-normal text-muted ms-2'
                    "
                    style="font-size: 16px; margin-bottom: 8px"
                >
                    Add Project Details
                </span>
            </div>
        </div>

        <!-- category selection -->
        <div v-if="!active">
            <div class="sectionTitle">Choose the Document Categories</div>
            <div class="row g-2 mb-3">
                <div
                    v-for="category in categories"
                    :key="category.id"
                    class="lg-col-20 xs-col-50"
                >
                    <div
                        class="documentType"
                        :class="{
                            selected:
                                document.category === category.customValue,
                        }"
                    >
                        <input
                            v-model="document.category"
                            :value="category.customValue"
                            class="hiddenRadio"
                            name="test"
                            type="radio"
                            @click="
                                currentCategory = markRaw(category.markRaw);
                                selectCategory(category);
                            "
                        />
                        <img
                            :alt="category.label"
                            :src="category.img"
                            style="
                                width: 70px;
                                height: 70px;
                                border-radius: 8px;
                            "
                        />
                        <div
                            class="documentTitle"
                            v-html="category.label"
                        ></div>
                    </div>
                </div>
            </div>

            <div v-if="selected" class="alert alert-light border-0 tip-badge">
                <strong class="muted-badge">{{
                    selected.label.replace(/<\/?[^>]+(>|$)/g, "")
                }}</strong>
                <span class="fontS-14 ms-3">
                    {{ selected.description }}
                </span>
            </div>

            <hr />
            <div
                class="d-flex justify-content-end mt-1 mb-3"
                style="color: #412cc9"
            >
                <button
                    class="mainBtn btn btn-primary"
                    type="button"
                    @click="next"
                >
                    Proceed to Add Document Details
                </button>
            </div>
        </div>

        <div v-if="active === 1">
            <component
                :is="currentCategory"
                :key="`cat-${document.category}-step-${active}`"
                v-bind="{ document, v$, formErrors, isUpdateForm }"
            />
            <hr />
            <div
                class="d-flex mt-1 mb-3"
                :class="{
                    'justify-content-end': preventBackToStep1 == 1,
                    'justify-content-between': preventBackToStep1 == 0,
                }"
                style="color: #412cc9"
            >
                <button
                    v-if="!preventBackToStep1"
                    class="outlineBtn btn btn-secondary me-2"
                    type="button"
                    @click="back"
                >
                    Back
                </button>
                <button
                    v-if="document.category == 1"
                    class="btn btn-primary"
                    type="button"
                    :disabled="!canProceedToStep3"
                    @click="next"
                >
                    Proceed to Add Project Details
                </button>
                <form-btn
                    v-else
                    :disabled="
                        loaderStore.isLoading || !canSubmitStep2 || disabled
                    "
                    :loading="loaderStore.isLoading || disabled"
                    class="btn btn-primary"
                    :label="submitLabel"
                    :no-spinner="!isSubmitting"
                    @click="submit"
                />
            </div>
        </div>

        <!-- Step 2: Upload -->
        <div
            v-if="active === 2 && document.category == 1"
            style="color: #412cc9"
        >
            <component
                @submit-disabled="submitDisabled"
                :is="Category1Step3"
                v-bind="{ document, v$, formErrors }"
            />
            <hr />
            <div class="d-flex justify-content-between mt-1 mb-3">
                <button
                    class="outlineBtn btn btn-secondary me-2"
                    type="button"
                    @click="back"
                >
                    Back
                </button>
                <form-btn
                    :disabled="loaderStore.isLoading || disabled"
                    :loading="loaderStore.isLoading || disabled"
                    class="btn btn-primary"
                    :label="submitLabel"
                    :no-spinner="!isSubmitting"
                    @click="submit"
                />
            </div>
        </div>
    </div>
    <cancelModal
        v-model="showCancelModal"
        text="Document"
        @cancelButtonClose="cancelButtonClose"
        @cancel="confirmCancel"
    />
</template>

<style lang="scss">
.bar-container {
    height: 50px;
    z-index: 99999;
    width: 100%;
    top: -16px;
    padding-top: 20px;
    margin-top: -20px;
}

.progress-bar {
    transition: width 500ms;
}

.documentTitle,
.sectionTitle {
    font-weight: normal;
}

.sectionTitle {
    color: #101828;
    font-size: 16px;
    margin-bottom: 24px;
}

.el-dialog__title {
    color: #46454d !important;
    font-size: 20px;
    font-weight: 700;
}

.step-circle {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    line-height: 1;
    font-size: 14px;
}

.stepper {
    background: white;
    padding-bottom: 16px;
    border-bottom: 1px solid #eaecf0;
}

.documentType {
    min-height: 200px !important;
    padding: 15px 0 30px 0 !important;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.documentType.selected .documentTitle {
    font-weight: 700 !important;
}

.el-dialog {
    width: 53% !important;
}
.el-dialog__body {
    padding-bottom: 5px;
}
</style>
